import desertBgm from '../../../assets/cat-running/bgm/desert-bgm.mp3'
import forestBgm from '../../../assets/cat-running/bgm/forest-bgm.mp3'
import iceBgm from '../../../assets/cat-running/bgm/ice-bgm.mp3'
import volcanoBgm from '../../../assets/cat-running/bgm/volcano-gbm.mp3'
import { CAT_RUNNING_STAGE_KEYS } from '../constants'

const STAGE_BGM_SRC = {
  forest: forestBgm,
  desert: desertBgm,
  ice: iceBgm,
  volcano: volcanoBgm,
}

const TARGET_VOLUME = 0.35
const FADE_IN_MS = 180
const MUTE_STORAGE_KEY = 'cat-running-bgm-muted'
const DEFAULT_STAGE_KEY = CAT_RUNNING_STAGE_KEYS[0]

let muted = false
let audioUnlocked = false
let activeStageKey = null
let desiredStageKey = null
let playGeneration = 0
let fadeFrameId = null
const audioByStage = {}

const readStoredMuted = () => {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const writeStoredMuted = (value) => {
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, value ? '1' : '0')
  } catch {
    // Ignore storage failures in private mode.
  }
}

const resolveStageKey = (stageKey) =>
  CAT_RUNNING_STAGE_KEYS.includes(stageKey) ? stageKey : DEFAULT_STAGE_KEY

const getStageAudio = (stageKey) => {
  const resolvedKey = resolveStageKey(stageKey)
  if (!audioByStage[resolvedKey]) {
    const audio = new Audio(STAGE_BGM_SRC[resolvedKey])
    audio.loop = true
    audio.preload = 'auto'
    audio.volume = 0
    audioByStage[resolvedKey] = audio
  }
  return audioByStage[resolvedKey]
}

const clearFade = () => {
  if (fadeFrameId) {
    window.cancelAnimationFrame(fadeFrameId)
    fadeFrameId = null
  }
}

const applyVolume = (audio, volume) => {
  if (!audio) return
  audio.volume = muted ? 0 : volume
}

const pauseOtherTracks = (keepAudio) => {
  Object.values(audioByStage).forEach((audio) => {
    if (audio === keepAudio) return
    audio.pause()
  })
}

const stopAllTracks = () => {
  clearFade()
  Object.values(audioByStage).forEach((audio) => {
    audio.pause()
    try {
      audio.currentTime = 0
    } catch {
      // Ignore seek failures before metadata is ready.
    }
    audio.volume = 0
  })
}

const fadeIn = (audio, generation) => {
  clearFade()
  if (muted) {
    audio.volume = 0
    return
  }

  const startedAt = performance.now()
  audio.volume = 0

  const step = (now) => {
    if (generation !== playGeneration) {
      fadeFrameId = null
      return
    }

    const progress = Math.min(1, (now - startedAt) / FADE_IN_MS)
    audio.volume = TARGET_VOLUME * progress

    if (progress < 1) {
      fadeFrameId = window.requestAnimationFrame(step)
      return
    }

    fadeFrameId = null
    applyVolume(audio, TARGET_VOLUME)
  }

  fadeFrameId = window.requestAnimationFrame(step)
}

/** 제스처 안에서 다른 스테이지 트랙도 unlock (재생 유지는 keep만) */
const unlockOtherTracksSync = (keepStageKey) => {
  const keepKey = resolveStageKey(keepStageKey)

  CAT_RUNNING_STAGE_KEYS.forEach((stageKey) => {
    if (stageKey === keepKey) return

    const audio = getStageAudio(stageKey)
    audio.volume = 0
    const playPromise = audio.play()
    if (!playPromise || typeof playPromise.then !== 'function') return

    playPromise
      .then(() => {
        audioUnlocked = true
        audio.pause()
        try {
          audio.currentTime = 0
        } catch {
          // ignore
        }
      })
      .catch(() => {})
  })
}

const beginStagePlayback = (stageKey, { fromGesture = false } = {}) => {
  const resolvedKey = resolveStageKey(stageKey)
  const nextAudio = getStageAudio(resolvedKey)
  desiredStageKey = resolvedKey

  if (muted) {
    return Promise.resolve(false)
  }

  if (activeStageKey === resolvedKey && !nextAudio.paused) {
    applyVolume(nextAudio, TARGET_VOLUME)
    audioUnlocked = true
    return Promise.resolve(true)
  }

  const generation = ++playGeneration
  pauseOtherTracks(nextAudio)

  if (nextAudio.paused) {
    try {
      nextAudio.currentTime = 0
    } catch {
      // ignore
    }
  }

  nextAudio.volume = 0
  const playPromise = nextAudio.play()

  if (fromGesture) {
    unlockOtherTracksSync(resolvedKey)
  }

  if (!playPromise || typeof playPromise.then !== 'function') {
    return Promise.resolve(false)
  }

  return playPromise
    .then(() => {
      if (generation !== playGeneration && desiredStageKey !== resolvedKey) {
        return false
      }
      audioUnlocked = true
      activeStageKey = resolvedKey
      fadeIn(nextAudio, playGeneration)
      return true
    })
    .catch(() => false)
}

export const catRunningBgm = {
  preload() {
    CAT_RUNNING_STAGE_KEYS.forEach((stageKey) => {
      getStageAudio(stageKey)
    })
  },

  isMuted() {
    return muted
  },

  isUnlocked() {
    return audioUnlocked
  },

  isPlaying() {
    const audio = activeStageKey ? audioByStage[activeStageKey] : null
    return Boolean(audio && !audio.paused)
  },

  getActiveStageKey() {
    return activeStageKey
  },

  /** 재생 중이면 볼륨만 복구 (점프 시 무음 재생 상태 보정) */
  ensureAudible() {
    if (muted) return false
    const audio = activeStageKey ? audioByStage[activeStageKey] : null
    if (!audio || audio.paused) return false
    applyVolume(audio, TARGET_VOLUME)
    return true
  },

  loadMutedPreference() {
    muted = readStoredMuted()
    return muted
  },

  setMuted(value) {
    muted = Boolean(value)
    writeStoredMuted(muted)

    if (muted) {
      const audio = activeStageKey ? audioByStage[activeStageKey] : null
      if (audio) audio.volume = 0
      return Promise.resolve(true)
    }

    const stageKey = desiredStageKey ?? activeStageKey ?? DEFAULT_STAGE_KEY
    return this.playStage(stageKey)
  },

  toggleMuted() {
    this.setMuted(!muted)
    return muted
  },

  /**
   * 사용자 제스처에서 호출. stageKey가 없으면 기본 스테이지로 실제 재생을 시작한다.
   * (모바일은 시작 버튼 제스처에서 재생을 걸어야 이후 점프/카운트다운이 안정적이다)
   */
  unlockFromUserGesture(preferredStageKey) {
    const stageKey = preferredStageKey ?? desiredStageKey ?? activeStageKey ?? DEFAULT_STAGE_KEY
    return beginStagePlayback(stageKey, { fromGesture: true })
  },

  playStage(stageKey) {
    return beginStagePlayback(stageKey, { fromGesture: false })
  },

  pause() {
    playGeneration += 1
    clearFade()
    Object.values(audioByStage).forEach((audio) => audio.pause())
  },

  stop() {
    playGeneration += 1
    activeStageKey = null
    desiredStageKey = null
    stopAllTracks()
  },
}

export default catRunningBgm
